import apiClient from '@/lib/api-client';
import { type AxiosError } from 'axios';

/**
 * Eén stap binnen de regel van één persoon (story 31.1 aan de serverkant, 31.2 hier).
 *
 * Dezelfde namen en dezelfde volgorde als de bron ze levert — vastleggen vóór valideren — maar de
 * gétallen zijn die van déze persoon. Een stap waarin diegene geen minuut droeg krijgt GEEN regel,
 * ook niet één met nul: leeg is geen nul.
 *
 * `tradeItems` telt de onderscheiden stukken in DEZE stap. De som over de stappen mag hóger liggen
 * dan `tradeItems` op de regel zelf — dat is één stuk dat door beide stappen ging, en dat verschil
 * is geen fout maar precies wat het kerncijfer "uitgevoerde behandelingen" laat zien.
 */
export interface HandlingTimePerPersonHandling {
  handling: string;
  processCode: string;
  /** Mensminuten van deze stap. `null` — nooit `0` — zodra er niets gemeten is. */
  minutes: number | null;
  sessions: number;
  tradeItems: number;
  /**
   * Story 34.1 — waar het werk van deze persoon in DEZE stap lag, per productcategorie.
   *
   * OPTIONEEL, en om dezelfde reden als `handlings` hieronder: tijdens een uitrol kan een antwoord
   * binnenkomen dat nog van vóór deze story komt. Een categorie waarin diegene niets afrondde
   * krijgt geen regel.
   *
   * De elf tellingen VERDELEN `tradeItems` hierboven: elk product hoort bij precies één categorie,
   * dus ze tellen op tot dat getal. Daarom mag "Levensmiddelen, 31 van 35" naast een kolom staan
   * die 35 zegt (AC-0).
   */
  categories?: HandlingTimePersonCategorie[];
}

/** Eén categorie binnen één stap van één persoon (story 34.1). */
export interface HandlingTimePersonCategorie {
  /** `null` is *overige categorieën* — de elfde regel, en nadrukkelijk niet "onbekend". */
  categoryCode: string | null;
  /** Onderscheiden producten van deze persoon in deze categorie, binnen deze stap. */
  tradeItems: number;
}

/**
 * Story 34.1 — één regel van het blok "waar ging de tijd heen per soort product".
 *
 * Hier is nul WEL een geldige waarde, anders dan bij `minutes` op een persoonsregel: de elf regels
 * zijn een vast kader en geen meetuitkomst. Een categorie met nul stuks zegt "er kwam in deze
 * periode niets van dat soort binnen", en dat is informatie.
 */
export interface HandlingTimeCategorieRegel {
  categoryCode: string | null;
  /**
   * Onderscheiden producten in deze categorie, binnen deze stap (AC-0).
   *
   * Hetzelfde soort getal als de kolom Stuks in de aandachtslijst, en de elf regels tellen samen op
   * tot het aantal stukken van díe stap. Hetzelfde product dat twee keer behandeld wordt telt één
   * keer — anders zou "per stuk" hier iets anders betekenen dan één blok hoger.
   */
  tradeItems: number;
  /** Mensminuten in deze categorie. De elf regels tellen samen op tot de tijd van díe stap. */
  minutes: number;
}

/** Eén blok: één stap, met zijn elf regels (story 34.1). */
export interface HandlingTimeCategorieBlok {
  handling: string;
  processCode: string;
  lines: HandlingTimeCategorieRegel[];
}

/**
 * De drie tijdvakken die de bron kent. Dezelfde namen als daar, want ze gaan ongewijzigd de vraag
 * in; een vierde waarde weigert de serverlaag vóór er een aanroep uitgaat.
 */
export type HandlingTimeBucketSize = 'day' | 'week' | 'month';

/**
 * Eén stap binnen één tijdvak van de reeks over de tijd.
 *
 * Let op wat hier NIET staat: `sessions`. Een tijdvakgrens is een vensterrand, en een sessie die
 * over zo'n rand loopt valt half buiten beeld. De serverlaag noemt het veld daarom niet en zod
 * strípt het; dit type spiegelt dat, zodat een scherm er nooit op kan gaan leunen.
 */
export interface HandlingTimeBucketHandling {
  handling: string;
  processCode: string;
  minutes: number | null;
  tradeItems: number;
}

/**
 * Eén tijdvak uit de reeks over de tijd (story 30.1 aan de bronkant).
 *
 * `from` en `to` zijn volledige tijdstippen in UTC en half open: `to` is het begin van het
 * volgende tijdvak en telt zelf niet mee. Een randtijdvak wordt NIET afgekapt op de periodegrens,
 * dus wie er een grafiek van tekent mag zo'n tijdvak niet als een volle week presenteren.
 */
export interface HandlingTimeBucket {
  from: string;
  to: string;
  /** De Nederlandse kalenderstart: `2026-08-24` bij dag en week, `2026-08` bij maand. */
  label: string;
  handlings: HandlingTimeBucketHandling[];
}

/**
 * Story 28-2 — de regels zoals procesmanagement ze levert, ongewijzigd.
 *
 * `userNameOutcome` is met opzet een gewone string en geen opsomming: komt er in de bron een
 * vierde uitkomst bij, dan moet die zichtbaar worden in plaats van het scherm te laten mislukken.
 * De drie die er vandaag zijn staan hieronder als constanten, zodat de vertaling ze kan opzoeken
 * en een onbekende uitkomst herkenbaar overblijft in plaats van als leeg veld te verdwijnen.
 */
export interface HandlingTimePerPersonRow {
  userId: number;
  /** Alleen aanwezig bij `named`. Nooit invullen met een verzonnen waarde. */
  userName?: string;
  userNameOutcome: string;
  /** Mensminuten. `null` — nooit `0` — zodra er niets gemeten is. */
  minutes: number | null;
  sessions: number;
  tradeItems: number;
  /**
   * Dezelfde persoon, uitgesplitst per stap. De vier velden hierboven blijven onveranderd:
   * `minutes` is de SOM over de stappen, `tradeItems` blijft ONTDUBBELD over de stappen heen.
   *
   * OPTIONEEL, en dat is een reviewbevinding van 27 augustus 2026. De serverlaag maakt er sinds
   * story 31.1 altijd een lijst van, maar een antwoord dat nog van vóór die uitrol komt draagt het
   * veld niet — een browser met een oude pagina tegen een nieuwe serverlaag, of andersom tijdens
   * een uitrol. Het type stond hier eerst als verplicht terwijl de toelichting én de lezer hem als
   * mogelijk afwezig behandelden; die twee spraken elkaar tegen, en een toets moest er een omweg
   * voor bouwen om een geldig oud antwoord na te maken.
   */
  handlings?: HandlingTimePerPersonHandling[];
}

export interface HandlingTimePerPersonResponse {
  data: HandlingTimePerPersonRow[];
  meta: {
    from: string;
    to: string;
    /**
     * De reeks over de tijd. AFWEZIG betekent "er is niet om een reeks gevraagd", een LEGE lijst
     * betekent "in dit venster is niets gemeten" — die twee mogen nooit samenvallen, anders tekent
     * een scherm een lege grafiek waar het niets hoort te tekenen. Story 31.2 vraagt er nog niet
     * om; het type staat er zodat de reeks bereikbaar is zodra 31.3 hem opvraagt.
     */
    buckets?: HandlingTimeBucket[];
    /**
     * Story 34.1 — het blok per soort product, één per stap.
     *
     * OPTIONEEL zodat een antwoord van vóór deze story het scherm niet laat mislukken; een LEGE
     * lijst betekent hetzelfde als afwezig, namelijk dat er geen blok te tekenen valt. Dat verschil
     * hoeft hier dus niet bewaard te blijven, anders dan bij `buckets` — daar zegt afwezig "er is
     * niet om een reeks gevraagd" en leeg "er is niets gemeten".
     */
    productCategories?: HandlingTimeCategorieBlok[];
  };
}

/** De uitkomsten die de dienst vandaag kent. Elk heeft een eigen tekst op het scherm. */
export const NAME_OUTCOMES = ['named', 'person-removed', 'no-user-record', 'automated-system'] as const;

/**
 * Een GLN is precies 13 cijfers. Tekst, zodat een voorloopnul blijft.
 */
export function isGeldigGln(gln: string): boolean {
  return /^[0-9]{13}$/.test(gln);
}

/**
 * De cijfers over een periode, en desgevraagd de reeks over de tijd erbij.
 *
 * `bucket` vraagt om ÉÉN reeks in één aanroep (story 31.3). Nadrukkelijk niet per tijdvak apart:
 * dat zou van een grafiek over een jaar een reeks van vijftig vragen maken op een route die per
 * vraag een halve seconde kan kosten. Zonder `bucket` blijft de vraag precies wat hij was — de
 * serverlaag geeft dan geen reeks terug, en dat is iets anders dan een lege reeks.
 */
export async function getHandlingTimePerPerson(
  period: { from: string; to: string },
  options: { bucket?: HandlingTimeBucketSize; userId?: number; gln?: string } = {},
): Promise<HandlingTimePerPersonResponse> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  if (options.bucket !== undefined) query.set('bucket', options.bucket);
  // `userId` beperkt de meting tot één persoon (story 31.4). Alleen wanneer erom gevraagd is: een
  // vraag die hem per ongeluk altijd meestuurt vervangt de cijfers van iedereen stil door die van
  // één iemand, en dat is precies het gevaar waar AC-10 tegen beschermt.
  if (options.userId !== undefined) query.set('userId', String(options.userId));
  // `gln` beperkt de meting tot de items van één partij. Afwezig betekent alle GLN's.
  if (options.gln !== undefined) query.set('gln', options.gln);
  const response = await apiClient.get<HandlingTimePerPersonResponse>(`/handling-time?${query.toString()}`);
  return response.data;
}

/**
 * De poort staat dicht: deze omgeving heeft geen leessleutel voor de behandeltijd.
 *
 * Dit is met opzet GEEN storing, en het scherm hoort het ook niet als storing te tonen. De
 * serverlaag houdt "poort dicht", "storing" en "niets gemeten" uit elkaar; op het scherm zag je
 * dat verschil niet terug — rood, en een knop "opnieuw proberen" die per definitie nooit helpt,
 * want een ontbrekende instelling verandert niet door de vraag te herhalen. Gezien op productie
 * op 24 augustus 2026, meteen na de uitrol.
 */
export function isHandlingTimeGateClosed(error: unknown): boolean {
  const response = (error as AxiosError<{ error?: { code?: string } }> | null)?.response;
  return response?.data?.error?.code === 'HANDLING_TIME_GATE_CLOSED';
}

/**
 * Story 31.5 — de twee normen, in minuten per stuk. `null` is "geen norm", nooit de waarde nul.
 *
 * Dezelfde vorm als `Normen` in `normen.ts`; die staat daar omdat het rekenwerk er niets van de
 * serverlaag voor nodig heeft, en deze staat hier omdat het antwoord van de serverlaag hem draagt.
 */
export interface HandlingTimeNormen {
  vastleggen: number | null;
  valideren: number | null;
}

/**
 * De normen ophalen bij het EIGEN punt van de behandeltijd, en nadrukkelijk niet bij
 * `/admin/settings`.
 *
 * Die beheerroute staat achter `admin` en eist een reden bij elke wijziging — samen sluit dat
 * precies de leidinggevende buiten die de norm hoort te zetten (spec 31.5, besluit 29 augustus
 * 2026).
 */
export async function getHandlingTimeNormen(): Promise<HandlingTimeNormen> {
  const response = await apiClient.get<{ data: HandlingTimeNormen }>('/handling-time/normen');
  return response.data.data;
}

/** De normen zetten. Geen reden erbij: wie de norm zette en wanneer vult de serverlaag zelf. */
export async function zetHandlingTimeNormen(normen: HandlingTimeNormen): Promise<HandlingTimeNormen> {
  const response = await apiClient.put<{ data: HandlingTimeNormen }>('/handling-time/normen', normen);
  return response.data.data;
}

/**
 * Vervolg behandeltijd-rapportage, CAP-3 — één rij per persoon en per etiket.
 *
 * Drie aparte velden die nooit door elkaar geteld worden: het tijdstip waarop iemand het etiket
 * OPENDE (`geopendAt`), het tijdstip waarop het is OPGESLAGEN (`opgeslagenAt`), en de bevestigde
 * MINUTEN van dat etiket (`minuten`). Een `null` betekent "gebeurde niet" en nooit een verzonnen tijd.
 */
export interface HandlingTimePerEtiketRij {
  userId: number;
  /** Alleen aanwezig bij `named`. Nooit invullen met een verzonnen waarde. */
  userName?: string;
  userNameOutcome: string;
  /** Het etiket: `GLN-GTIN-doelmarkt`. */
  tradeItemId: string;
  gtin: string;
  geopendAt: string | null;
  opgeslagenAt: string | null;
  minuten: number;
  opgeslagen: boolean;
}

export interface HandlingTimePerEtiketResponse {
  data: HandlingTimePerEtiketRij[];
  meta: { from: string; to: string; gln?: string; total: number };
}

/**
 * Het per-etiket antwoord over een periode. Zelfde periode, GLN en persoonsvraag als het overzicht
 * per persoon; een afwezig GLN betekent alle GLN's, en wordt dus niet meegestuurd.
 */
export async function getHandlingTimePerEtiket(
  period: { from: string; to: string },
  options: { userId?: number; gln?: string } = {},
): Promise<HandlingTimePerEtiketResponse> {
  const query = new URLSearchParams({ from: period.from, to: period.to });
  if (options.userId !== undefined) query.set('userId', String(options.userId));
  if (options.gln !== undefined) query.set('gln', options.gln);
  const response = await apiClient.get<HandlingTimePerEtiketResponse>(`/handling-time/per-etiket?${query.toString()}`);
  return response.data;
}

/** De stappen van het werkvenster, zoals de bron ze kent. `both` is beide stappen samen. */
export type HandlingTimeWorkWindowStep = 'both' | 'vastleggen' | 'valideren';

/**
 * Behandeltijd-rapport, CAP-2 en CAP-3 — één dag van één medewerker.
 *
 * `null` is "geen eind" en nooit een nul: een dag zonder FINISHED heeft geen venster en geen gat.
 */
export interface HandlingTimeWorkWindowRow {
  userId: number;
  day: string;
  windowStart: string | null;
  windowEnd: string | null;
  windowMinutes: number | null;
  gapMinutes: number | null;
}

export interface HandlingTimeWorkWindowResponse {
  data: HandlingTimeWorkWindowRow[];
  meta: { from: string; to: string; step: HandlingTimeWorkWindowStep };
}

/**
 * Het werkvenster en het gat per medewerker per dag. Alleen periode en stap, en nooit een partij:
 * het venster gaat over de medewerker, ongeacht partij.
 */
export async function getHandlingTimeWorkWindow(
  period: { from: string; to: string },
  step: HandlingTimeWorkWindowStep,
): Promise<HandlingTimeWorkWindowResponse> {
  const query = new URLSearchParams({ from: period.from, to: period.to, step });
  const response = await apiClient.get<HandlingTimeWorkWindowResponse>(`/handling-time/work-window?${query.toString()}`);
  // Een 200 zonder rijenlijst (een proxy-pagina bijvoorbeeld) is een storing, nooit een lege periode:
  // anders zouden alle cellen een streepje tonen alsof er niets gewerkt is.
  if (!Array.isArray(response.data?.data)) throw new Error('werkvenster: onverwacht antwoord van de route');
  return response.data;
}
